import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Tooltip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Backdrop,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  People as PeopleIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { useConfig } from '../contexts/ConfigContext';
import NumericField from './NumericField';

export default function Teams() {
  const navigate = useNavigate();
  const { 
    getCountryOptions, 
    getTeamRoleOptions, 
    getCurrencyOptions, 
    getEntityTypeOptions, 
    getPaymentMethodOptions,
    loading: configLoading 
  } = useConfig();

  // Get dynamic configuration options
  const roles = getTeamRoleOptions();
  const countries = getCountryOptions();
  const currencies = getCurrencyOptions();
  const statuses = ['Active', 'Inactive', 'On Leave', 'Terminated'];
  const entityTypes = getEntityTypeOptions();
  const paymentWays = getPaymentMethodOptions();

  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    country: '',
    status: '',
    role: '',
    type: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    role: '',
    location: '',
    currency: 'USD',
    notes: '',
    status: 'Active',
    type: 'Team',
    defaultPercentage: 0,
    paymentWay: 'Direct',
    paymentInfo: {
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      accountHolder: ''
    },
            payables: 0,
        pastPayments: 0,
        expectedPayouts: 0,
        locationDetails: { location: '', country: '', transportPercent: 0 }
  });

  // Firestore operations
  const fetchEntities = async () => {
    try {
      setLoading(true);
      const entitiesRef = collection(db, 'entities');
      const q = query(entitiesRef, orderBy('entityName', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const entitiesData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          entitiesData.push({
            id: doc.id,
            ...data,
            defaultPercentage: data.defaultPercentage || 0,
            payables: data.payables || 0,
            pastPayments: data.pastPayments || 0,
            expectedPayouts: data.expectedPayouts || 0,
            paymentInfo: data.paymentInfo || {},
            locationDetails: data.locationDetails || { location: '', country: '', transportPercent: 0 },
          });
        });
        setTeams(entitiesData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching entities:', error);
      setSnackbar({ open: true, message: 'Error loading entities', severity: 'error' });
      setLoading(false);
    }
  };

  const addEntity = async (entityData) => {
    try {
      setSaving(true);
      const docRef = await addDoc(collection(db, 'entities'), {
        ...entityData,
        defaultPercentage: Number(entityData.defaultPercentage) || 0,
        payables: Number(entityData.payables) || 0,
        pastPayments: Number(entityData.pastPayments) || 0,
        expectedPayouts: Number(entityData.expectedPayouts) || 0,
        paymentInfo: entityData.paymentInfo || {},
        locationDetails: {
          location: entityData.locationDetails?.location || '',
          country: entityData.locationDetails?.country || entityData.location,
          transportPercent: Number(entityData.locationDetails?.transportPercent) || 0,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Entity created successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding entity:', error);
      setSnackbar({ open: true, message: 'Error creating entity', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateEntity = async (entityId, entityData) => {
    try {
      setSaving(true);
      const entityRef = doc(db, 'entities', entityId);
      await updateDoc(entityRef, {
        ...entityData,
        defaultPercentage: Number(entityData.defaultPercentage) || 0,
        payables: Number(entityData.payables) || 0,
        pastPayments: Number(entityData.pastPayments) || 0,
        expectedPayouts: Number(entityData.expectedPayouts) || 0,
        paymentInfo: entityData.paymentInfo || {},
        locationDetails: {
          location: entityData.locationDetails?.location || '',
          country: entityData.locationDetails?.country || entityData.location,
          transportPercent: Number(entityData.locationDetails?.transportPercent) || 0,
        },
        updatedAt: serverTimestamp(),
      });
      
      setSnackbar({ open: true, message: 'Entity updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating entity:', error);
      setSnackbar({ open: true, message: 'Error updating entity', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteEntity = async (entityId) => {
    try {
      const entityRef = doc(db, 'entities', entityId);
      await deleteDoc(entityRef);
      setSnackbar({ open: true, message: 'Entity deleted successfully!', severity: 'info' });
    } catch (error) {
      console.error('Error deleting entity:', error);
      setSnackbar({ open: true, message: 'Error deleting entity', severity: 'error' });
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = fetchEntities();
    
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, teams]);

  const applyFilters = () => {
    let filtered = teams;
    
    if (filters.country) {
      filtered = filtered.filter(team => team.location === filters.country);
    }
    if (filters.status) {
      filtered = filtered.filter(team => team.status === filters.status);
    }
    if (filters.role) {
      filtered = filtered.filter(team => team.role === filters.role);
    }
    if (filters.type) {
      filtered = filtered.filter(team => team.type === filters.type);
    }
    
    setFilteredTeams(filtered);
  };

  const handleOpenDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({ 
        ...team,
        paymentInfo: team.paymentInfo || {
          bankName: '',
          accountNumber: '',
          routingNumber: '',
          swiftCode: '',
          accountHolder: ''
        },
        locationDetails: team.locationDetails || { location: '', country: '', transportPercent: 0 }
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        email: '',
        number: '',
        role: '',
        location: '',
        currency: 'USD',
        notes: '',
        status: 'Active',
        type: 'Team',
        defaultPercentage: 0,
        paymentWay: 'Direct',
        paymentInfo: {
          bankName: '',
          accountNumber: '',
          routingNumber: '',
          swiftCode: '',
          accountHolder: ''
        },
        payables: 0,
        pastPayments: 0,
        expectedPayouts: 0,
        locationDetails: { location: '', country: '', transportPercent: 0 }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
  };

  const handleSubmit = async () => {
    try {
    if (editingTeam) {
        await updateEntity(editingTeam.id, formData);
    } else {
        await addEntity(formData);
    }
    handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this entity? This action cannot be undone.')) {
      try {
        await deleteEntity(id);
      } catch (error) {
        // Error handling is done in the deleteEntity function
      }
    }
  };

  const handleExpandRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleLocationDetailChange = (field, value) => {
    const newLocationDetails = { ...formData.locationDetails };
    newLocationDetails[field] = value;
    setFormData({ ...formData, locationDetails: newLocationDetails });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      case 'On Leave': return 'warning';
      case 'Terminated': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
             Entities
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage team members and track their financial information
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ mr: 1 }}
          >
            Filters
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Team Member
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={filters.country}
                  label="Country"
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map(country => (
                    <MenuItem key={country.code} value={country.name}>{country.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {roles.map(role => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {entityTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Teams Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Default %</TableCell>
                <TableCell>Payment Way</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Currency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payables</TableCell>
                <TableCell>Past Payments</TableCell>
                <TableCell>Expected Payouts</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTeams.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    No entities found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredTeams.map((team) => (
                <React.Fragment key={team.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleExpandRow(team.id)}
                      >
                        {expandedRows[team.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{team.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {team.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {team.number}
                      </Box>
                    </TableCell>
                    <TableCell>{team.role}</TableCell>
                    <TableCell>
                      <Chip 
                        label={team.type} 
                        color={team.type === 'Team' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {team.defaultPercentage}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={team.paymentWay} 
                        color={team.paymentWay === 'Direct' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {team.location}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={team.currency} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={team.status} 
                        color={getStatusColor(team.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {team.currency} {team.payables.toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {team.currency} {team.pastPayments.toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        {team.currency} {team.expectedPayouts.toLocaleString()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(team)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(team.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row - Location Details */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                      <Collapse in={expandedRows[team.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Location Details & Notes
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    Office Locations & Payment Info
                                  </Typography>
                                  {/* Payment Information Display */}
                                  <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Banking Details
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Bank:</strong> {team.paymentInfo?.bankName || 'Not provided'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Account Holder:</strong> {team.paymentInfo?.accountHolder || 'Not provided'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Account:</strong> {team.paymentInfo?.accountNumber || 'Not provided'}
                                    </Typography>
                                    {team.paymentInfo?.swiftCode && (
                                      <Typography variant="body2">
                                        <strong>SWIFT:</strong> {team.paymentInfo.swiftCode}
                                      </Typography>
                                    )}
                                  </Box>
                                                                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Office Location
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Location:</strong> {team.locationDetails?.location || 'Not specified'}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Country:</strong> {team.locationDetails?.country || 'Not specified'}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>Transport %:</strong> {team.locationDetails?.transportPercent || 0}%
                                      </Typography>
                                    </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    Additional Info
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Type:</strong> {team.type}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Default %:</strong> {team.defaultPercentage}%
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>
                                    <strong>Payment Way:</strong> {team.paymentWay}
                                  </Typography>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Notes
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {team.notes || 'No notes available'}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Edit Team Member' : 'Add Team Member'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roles.map(role => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  {entityTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location}
                  label="Location"
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                >
                  {countries.map(country => (
                    <MenuItem key={country.code} value={country.name}>{country.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  label="Currency"
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  {currencies.map(currency => (
                    <MenuItem key={currency.code} value={currency.code}>{currency.name} ({currency.symbol})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  {statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <NumericField
                fullWidth
                label="Default Percentage"
                value={formData.defaultPercentage}
                onValueChange={(n) => setFormData({ ...formData, defaultPercentage: n ?? 0 })}
                mode="decimal"
                decimals={1}
                suffix="%"
                min={0}
                max={100}
                helperText="Default commission/cut percentage"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Way</InputLabel>
                <Select
                  value={formData.paymentWay}
                  label="Payment Way"
                  onChange={(e) => setFormData({ ...formData, paymentWay: e.target.value })}
                >
                  {paymentWays.map(way => (
                    <MenuItem key={way} value={way}>{way}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Payables"
                type="number"
                value={formData.payables}
                onChange={(e) => setFormData({ ...formData, payables: Number(e.target.value) })}
              />
            </Grid> */}
            {/* <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Past Payments"
                type="number"
                value={formData.pastPayments}
                onChange={(e) => setFormData({ ...formData, pastPayments: Number(e.target.value) })}
              />
            </Grid> */}
            {/* <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Expected Payouts"
                type="number"
                value={formData.expectedPayouts}
                onChange={(e) => setFormData({ ...formData, expectedPayouts: Number(e.target.value) })}
              />
            </Grid> */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            
            {/* Payment Information Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    value={formData.paymentInfo.bankName}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      paymentInfo: { ...formData.paymentInfo, bankName: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Holder"
                    value={formData.paymentInfo.accountHolder}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      paymentInfo: { ...formData.paymentInfo, accountHolder: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={formData.paymentInfo.accountNumber}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      paymentInfo: { ...formData.paymentInfo, accountNumber: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Routing Number"
                    value={formData.paymentInfo.routingNumber}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      paymentInfo: { ...formData.paymentInfo, routingNumber: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SWIFT Code"
                    value={formData.paymentInfo.swiftCode}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      paymentInfo: { ...formData.paymentInfo, swiftCode: e.target.value }
                    })}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Location Details Section */}
            {/* <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                Location Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={formData.locationDetails.location}
                    onChange={(e) => handleLocationDetailChange('location', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.locationDetails.country}
                      label="Country"
                      onChange={(e) => handleLocationDetailChange('country', e.target.value)}
                    >
                      {countries.map(country => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Transport %"
                    type="number"
                    value={formData.locationDetails.transportPercent}
                    onChange={(e) => handleLocationDetailChange('transportPercent', Number(e.target.value))}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </Grid> */}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (editingTeam ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Team Members
              </Typography>
              <Typography variant="h4">
                {filteredTeams.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Members
              </Typography>
              <Typography variant="h4">
                {filteredTeams.filter(team => team.status === 'Active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Payables
              </Typography>
              <Typography variant="h4">
                ${filteredTeams.reduce((sum, team) => sum + team.payables, 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Expected Payouts
              </Typography>
              <Typography variant="h4">
                ${filteredTeams.reduce((sum, team) => sum + team.expectedPayouts, 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

             {/* Snackbar for notifications */}
       <Snackbar
         open={snackbar.open}
         autoHideDuration={6000}
         onClose={() => setSnackbar({ ...snackbar, open: false })}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
       >
         <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
           {snackbar.message}
         </Alert>
       </Snackbar>

       {/* Backdrop for loading */}
       <Backdrop
         sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
         open={loading || saving}
       >
         <CircularProgress color="inherit" />
       </Backdrop>
    </Container>
  );
} 